import { Paths } from "@app/routes";
import {
  AppMode,
  AuthGuard,
  ErrorBoundary,
  SessionExpiredDialog,
  WithPermission,
} from "@atlas/composite";
import {
  PermissionsProvider,
  useTranslation,
  useZodSearchParams,
} from "@atlas/sdk";
import { CatalogMode } from "@enums";
import {
  AdministerPage,
  CatalogInfoWrapper,
  CatalogInstallPage,
  CatalogTypeManagerPage,
  CdvManagerPage,
  CreateEditCatalogPage,
  CreateEditCatalogTypePage,
  DeploymentDetails,
  ExportListPage,
  Home,
  HubManagerPage,
  IdpDashboardPage,
  MyProfilePage,
  PackageCheckout,
  PackageManagerPage,
  SubscriptionPage,
  SupportCenterPage,
} from "@modules";
import { useHubCdv, useUserInfo, useUserPermissions } from "@query";
import {
  APP_BASE_PATH,
  BreadcrumbProvider,
  HUB_PERM,
  isCatalogTypePermittedByCdvEmail,
  MicroFrontendKeepAliveHost,
  MicroFrontendKeepAliveProvider,
  MicroFrontendRoutesShell,
  mapApiPermissionsToHubPermissions,
  Shell,
} from "@shared";
import { useEffect, useMemo } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { z } from "zod";
import "./index.css";

const appSearchParamsSchema = z.object({
  view: z.string().optional(),
  tab: z.string().optional(),
  catalogId: z.coerce.number().optional(),
  solution: z.coerce.number().optional(),
  solutionCatalogId: z.coerce.number().optional(),
  packageId: z.coerce.number().optional(),
  deploymentId: z.coerce.number().optional(),
  typeId: z.coerce.number().optional(),
  version: z.string().optional(),
  fromTab: z.string().optional(),
});

/** Preserves query string for old `/package-install` links. */
function LegacyPackageInstallPathRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${Paths.PackageManager}${search}`} replace />;
}

function PackageManagerGate() {
  const { view, tab, packageId, deploymentId, fromTab } = useZodSearchParams(
    appSearchParamsSchema,
  );

  const isPackagesOrDeploymentsTab =
    tab === Paths.PackageManagerViewPackages ||
    tab === Paths.PackageDeployments ||
    view === Paths.PackageManagerViewPackages;

  if (isPackagesOrDeploymentsTab) {
    return <PackageManagerPage />;
  }
  if (deploymentId != null && deploymentId > 0) {
    return <DeploymentDetails />;
  }
  if (packageId != null && packageId > 0) {
    if (fromTab === "packages") {
      return <PackageCheckout />;
    }
    return <CatalogInstallPage packageId={packageId} />;
  }
  return <PackageCheckout />;
}

function PortalAuthenticatedLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: userPermissions, isLoading: isPermissionsLoading } =
    useUserPermissions();

  const { data: userInfo, isLoading: isUserInfoLoading } =
    useUserInfo(APP_BASE_PATH);

  const apiPermissions = useMemo(
    () => mapApiPermissionsToHubPermissions(userPermissions),
    [userPermissions],
  );
  const apiHasCatalogTypeManage = apiPermissions.includes(
    HUB_PERM.CATALOG_TYPE_MANAGE,
  );

  const { data: cdvs = [], isLoading: isCdvsLoading } = useHubCdv({
    enabled: apiHasCatalogTypeManage,
  });

  const permissions = useMemo(() => {
    if (
      !apiHasCatalogTypeManage ||
      !userInfo ||
      isCatalogTypePermittedByCdvEmail(userInfo.email, cdvs)
    ) {
      return apiPermissions;
    }

    return apiPermissions.filter((p) => p !== HUB_PERM.CATALOG_TYPE_MANAGE);
  }, [apiPermissions, apiHasCatalogTypeManage, userInfo, cdvs]);

  const permissionsReady =
    !isPermissionsLoading &&
    !isUserInfoLoading &&
    (!apiHasCatalogTypeManage || !isCdvsLoading) &&
    userPermissions !== undefined;

  const shellLoading =
    isPermissionsLoading ||
    isUserInfoLoading ||
    (apiHasCatalogTypeManage && isCdvsLoading);

  return (
    <PermissionsProvider permissions={permissions}>
      <BreadcrumbProvider>
        <MicroFrontendKeepAliveProvider>
          <Shell isLoading={shellLoading}>
            <div className="relative flex min-h-0 flex-1 flex-col">
              <MicroFrontendKeepAliveHost />
              <MicroFrontendRoutesShell>
                {permissionsReady ? (
                  <ErrorBoundary
                    title={t("hub.error.load_failed_title", {
                      defaultValue: "Unable to load this page",
                    })}
                    onRetry={() =>
                      navigate(`/${Paths.Home}`, { replace: true })
                    }>
                    <Outlet />
                  </ErrorBoundary>
                ) : null}
              </MicroFrontendRoutesShell>
            </div>
          </Shell>
        </MicroFrontendKeepAliveProvider>
      </BreadcrumbProvider>
    </PermissionsProvider>
  );
}

export default function App() {
  const { t } = useTranslation();
  const { catalogId, packageId, typeId, version } = useZodSearchParams(
    appSearchParamsSchema,
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has("mobileLogin")) {
      const raw = queryParams.get("mobileLogin")?.toLowerCase();
      if (raw === "true") {
        sessionStorage.setItem("isMobileFlow", "true");
      } else if (raw === "false") {
        sessionStorage.setItem("isMobileFlow", "false");
      } else {
        sessionStorage.removeItem("isMobileFlow");
      }
    }
  }, []);

  return (
    <>
      <Routes>
        <Route
          element={
            <AuthGuard
              appBasePath={APP_BASE_PATH}
              localizationProps={{
                loginIdLabel: t("auth.login.login_id", {
                  defaultValue: "Login Id",
                }),
                nextButtonText: t("auth.login.next", { defaultValue: "Next" }),
                usernamePlaceholder: t("auth.login.login_id", {
                  defaultValue: "Login Id",
                }),
                selectCompanyHeading: t("auth.login.select_company_heading", {
                  defaultValue: "Sign in",
                }),
                selectCompanyPlaceholder: t(
                  "auth.login.select_company_placeholder",
                  { defaultValue: "Select company" },
                ),
                selectCompanyErrorText: t("auth.login.select_company_error", {
                  defaultValue: "Select a company to continue",
                }),
                emailRequiredText: t("auth.login.email_required", {
                  defaultValue: "Enter email to continue.",
                }),
                emailInvalidText: t("auth.login.email_invalid", {
                  defaultValue: "Enter a valid email address.",
                }),
                backButtonText: t("auth.login.back", { defaultValue: "Back" }),
                supportLabelText: t("auth.login.support_label", {
                  defaultValue: "Having trouble?",
                }),
                supportLinkText: t("auth.login.support_link", {
                  defaultValue: "Contact customer support",
                }),
                supportLinkURL: "https://www.ironmountain.com/support",
                privacyNoticeURL:
                  "https://www.ironmountain.com/en-in/utility/legal/privacy-policy",
              }}
              defaultRedirectPath={Paths.Home}
              appMode={AppMode.Portal}>
              <PortalAuthenticatedLayout />
            </AuthGuard>
          }>
          <Route
            path="/"
            element={<Navigate to={`/${Paths.Home}`} replace />}
          />
          <Route path={Paths.Home} element={<Home />} />
          <Route
            path={Paths.CatalogManager}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_VIEW]}>
                <HubManagerPage />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogManager}/${Paths.CreateCatalog}`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_CREATE]}>
                <CreateEditCatalogPage />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogManager}/${Paths.EditCatalog}`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_EDIT]}>
                <CreateEditCatalogPage
                  catalogId={catalogId}
                  version={version}
                />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogManager}/${Paths.CatalogDetails}`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_VIEW]}>
                <CatalogInfoWrapper
                  mode={CatalogMode.Details}
                  catalogId={catalogId}
                  version={version}
                />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogManager}/${Paths.ReviewCatalog}`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_REVIEW_VIEW]}>
                <CatalogInfoWrapper
                  mode={CatalogMode.Review}
                  catalogId={catalogId}
                  version={version}
                />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogManager}/${Paths.SubscribeSolution}`}
            element={
              <WithPermission showFallback permissions={[HUB_PERM.CATALOG_BUY]}>
                <CatalogInstallPage packageId={packageId} />
              </WithPermission>
            }
          />
          <Route
            path={Paths.PackageInstall}
            element={<LegacyPackageInstallPathRedirect />}
          />
          <Route
            path={Paths.PackageManager}
            element={
              <WithPermission showFallback permissions={[HUB_PERM.CATALOG_BUY]}>
                <PackageManagerGate />
              </WithPermission>
            }
          />
          <Route
            path={Paths.CdvManager}
            element={
              <WithPermission showFallback permissions={[HUB_PERM.CDV_MANAGE]}>
                <CdvManagerPage />
              </WithPermission>
            }
          />
          <Route
            path={Paths.CatalogTypeManager}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_TYPE_MANAGE]}>
                <CatalogTypeManagerPage />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogTypeManager}/${Paths.CreateCatalogType}`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_TYPE_MANAGE]}>
                <CreateEditCatalogTypePage typeId={typeId} />
              </WithPermission>
            }
          />
          <Route
            path={`${Paths.CatalogTypeManager}/${Paths.EditCatalogType}`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.CATALOG_TYPE_MANAGE]}>
                <CreateEditCatalogTypePage typeId={typeId} />
              </WithPermission>
            }
          />
          <Route
            path={Paths.Administer}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.ADMINISTER_ACCESS]}>
                <AdministerPage />
              </WithPermission>
            }
          />
          <Route
            path={Paths.Subscription}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.SUBSCRIPTION_MANAGE]}>
                <SubscriptionPage />
              </WithPermission>
            }
          />
          <Route
            path={Paths.ExportList}
            element={
              <WithPermission
                showFallback
                permissions={[
                  HUB_PERM.PACKAGE_TRANSFER,
                  HUB_PERM.CATALOG_TRANSFER,
                ]}>
                <ExportListPage />
              </WithPermission>
            }
          />
          <Route path={Paths.MyProfile} element={<MyProfilePage />} />
          <Route path={Paths.SupportCenter} element={<SupportCenterPage />} />
          <Route
            path={`${Paths.Dashboard}/*`}
            element={
              <WithPermission
                showFallback
                permissions={[HUB_PERM.DASHBOARD_ACCESS]}>
                <IdpDashboardPage />
              </WithPermission>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SessionExpiredDialog redirectToRoot />
    </>
  );
}
